import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concat, Observable, of, throwError } from 'rxjs';
import { concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { UrlService } from '../_shared/services/url.service';
import { AppService } from '../_shared/services/app.service';
import { PrintTypeLong } from '../_shared/models/print-type';

interface TaskDetails {
    id: string;
    state: string;
}

interface DocumentFile {
    objectType: string;
    documentUri: string;
    filename: string;
    mimeType: string;
}

export interface PdfDownload {
    url: string;
    type: string;
}

export interface PdfDownloadResult {
    isError: boolean;
    downloads: PdfDownload[];
}

@Injectable({
    providedIn: 'root',
})
export class NextStepsDataService {
    constructor(private urlService: UrlService, private http: HttpClient, private appService: AppService) {}

    public fetchOfferPdf(sapDocumentId: string, printType: PrintTypeLong): Observable<PdfDownloadResult> {
        // check if pdf is already available. if not, start generation process
        return this.tryDownload(sapDocumentId).pipe(
            concatMap(({ downloads }) =>
                downloads.length ? of({ isError: false, downloads }) : this.fetchPrintOfferPdf(sapDocumentId, printType)
            )
        );
    }

    private fetchPrintOfferPdf(sapDocumentId: string, printType: PrintTypeLong): Observable<PdfDownloadResult> {
        // first request to initiate task of generating pdf
        return this.createPdfTask(sapDocumentId, printType).pipe(
            delay(2000),
            // second request to check the state of pdf task (interval of 2000)
            concatMap(({ taskId }) => this.waitForTaskCompletion(taskId, sapDocumentId))
        );
    }

    private tryDownload(sapDocumentId: string): Observable<PdfDownloadResult> {
        return this.getFileList(sapDocumentId).pipe(
            map((list) => {
                const offers = list.filter((entry) => entry.objectType.startsWith('OFFER'));
                const downloads = offers.map((offer) => ({
                    url: `${environment.http.quotation}files/${sapDocumentId}/${offer.documentUri}`,
                    type: offer.objectType,
                }));
                return {
                    isError: false,
                    downloads,
                };
            })
        );
    }

    private waitForTaskCompletion(taskId: string, sapDocumentId: string) {
        return this.getTaskDetails(taskId).pipe(
            concatMap((res) => this.checkState(res.state, sapDocumentId)),
            retryWhen((error) =>
                error.pipe(delay(4000), take(15), (o) => concat(o, throwError('Retry limit exceeded!')))
            )
        );
    }

    private checkState(state: string, sapDocumentId: string): Observable<PdfDownloadResult> {
        if (['FINISHED_WITH_ERRORS', 'TERMINATED', 'FINISHED_ABORTED', 'FAILED'].includes(state)) {
            return of({ isError: true, downloads: [] });
        } else if (['FINISHED', 'SUCCEEDED'].includes(state)) {
            return this.tryDownload(sapDocumentId);
        } else {
            throw new Error();
        }
    }

    private createPdfTask(documentNumber: string, printType: string) {
        return this.createTask({
            documentNumber,
            printType,
            useExistingOfferPdf: false,
            sendEmailToSalesRep: true,
        });
    }

    private getFileList(sapDocumentId: string) {
        return this.http.get<DocumentFile[]>(`${environment.http.quotation}files/${sapDocumentId}`);
    }

    private getTaskDetails(taskId: string): Observable<TaskDetails> {
        return this.http.get<TaskDetails>(`${environment.http.quotation}offers/tasks/${taskId}`);
    }

    private createTask(payload: {
        documentNumber: string;
        useExistingOfferPdf: boolean;
        sendEmailToSalesRep: boolean;
        printType?: string;
    }) {
        const url = `${environment.http.quotation}offers/tasks`;
        return this.http.post<{ taskId: string }>(url, {
            type: 'EXPORT_OFFER',
            input: {
                ...payload,
                usecase: 'quote-assist-pro',
                locale: this.appService.getLanguageAsLocale(),
            },
        });
    }

    public getAttachments(id: string, isLegacy: boolean): Observable<any> {
        const url = isLegacy
            ? this.urlService.getUrl('next-steps.attachments', { id })
            : `${environment.http.quotation}quotations/quote-assist-pro/quotes/${id}/attachments`;
        return this.http.get(url);
    }

    public deleteAttachment(id: string, attachmentId: string, isLegacy: boolean): Observable<boolean> {
        const url = isLegacy
            ? this.urlService.getUrl('next-steps.attachment.delete', { quoteId: id, attachmentId })
            : `${environment.http.quotation}quotations/quote-assist-pro/quotes/${id}/attachments/${attachmentId}`;
        return this.http.delete<boolean>(url).pipe(map(() => true));
    }

    public downloadAttachment(id: string, documentUri: string, isLegacy: boolean) {
        return isLegacy
            ? this.urlService.getUrl('next-steps.attachment.download', { id, documentUri })
            : `${environment.http.quotation}quotations/quote-assist-pro/quotes/${id}/attachments/${documentUri}`;
    }

    public getQuoteEmailText(
        id: string,
        language: string,
        country: string,
        isLegacy: boolean
    ): Observable<{ text: string }> {
        const params = { language, country };
        const url = isLegacy
            ? `${environment.http.baseUrl}quote/${id}/revision/0/mail-text`
            : `${environment.http.quotation}quotations/quote-assist-pro/quotes/${id}/mail-text`;
        return this.http.get<{ text: string }>(url, { params });
    }

    getEmailRecipients(customerNumber: string) {
        return this.http.get(`${environment.http.companyUrl}${customerNumber}/contacts`);
    }

    public getExportTypes(salesOrg: string) {
        return this.http.get(`${environment.http.quotation}offers/export/types`, { params: { salesOrg } });
    }
}
